import { ConnectionMode } from './connection-mode.enum';
import { IntegrationVendor } from './integration-vendor.enum';
import { VendorDefinition } from './vendor-definition.model';

/**
 * The canonical list of vendor definitions shown on the Integrations page.
 * Adding a new vendor: append an entry here + create its connect-modal component.
 */
export const VENDOR_DEFINITIONS: VendorDefinition[] = [
    {
        vendor: IntegrationVendor.BEXIO,
        displayNameKey: 'aumico_companies_integrations_name_bexio',
        descriptionKey: 'aumico_companies_integrations_bexio_description',
        logoSrc: '/assets/hamIcons/bexio-logo.svg',
        bannerBgColor: '#e8f5f2',
        // Per-company: Bexio's connection + connect flow are owned by
        // aumico-lib-companies-fe. The company page supplies its state and opens
        // its modal; this lib only renders the catalog card in the unified grid.
        scope: 'per-company',
        mode: ConnectionMode.OAUTH_AUTH_CODE,
        connectModalType: 'bexio',
    },
    {
        vendor: IntegrationVendor.D365_BC,
        displayNameKey: 'aumico_integration_d365bc_displayname',
        descriptionKey: 'aumico_integration_d365bc_description',
        logoSrc: '/assets/hamIcons/d365bc-logo.svg',
        bannerBgColor: '#E6EEF8',
        scope: 'per-org',
        mode: ConnectionMode.OAUTH_CLIENT_CREDENTIALS,
        connectModalType: 'd365bc',
        supportsAutoMatch: true,
        setupFields: [
            {
                key: 'environmentName',
                labelKey: 'aumico_d365bc_step_config_environment_label',
                helpTextKey: 'aumico_d365bc_step_config_environment_help',
                type: 'text',
                required: true,
                defaultValue: 'production',
            },
            {
                key: 'displayName',
                labelKey: 'aumico_integration_setup_displayname_label',
                type: 'text',
                // Required: matches D365BcConnectModalComponent.isFormValid (enforces a
                // non-empty display name) and the "Pflichtfeld" help text — single source of truth.
                required: true,
            },
        ],
    },
    {
        vendor: IntegrationVendor.ABACUS,
        displayNameKey: 'aumico_integration_abacus_displayname',
        descriptionKey: 'aumico_integration_abacus_description',
        logoSrc: '/assets/hamIcons/generic-integration-logo.svg',
        bannerBgColor: '#F3F4F6',
        scope: 'per-org',
        mode: ConnectionMode.OAUTH_CLIENT_CREDENTIALS,
        connectModalType: 'abacus',
        // Abacus AbaReport has no enumerable Mandant listing API, so the user
        // must type each Mandanten ID per Aumico Company mapping. No auto match.
        supportsAutoMatch: false,
        // Documentation only: the actual form lives hand coded in
        // AbacusConnectModalComponent (clientSecret needs `type=password` and
        // bespoke validation that a generic renderer doesn't cover).
        setupFields: [
            {
                key: 'displayName',
                labelKey: 'aumico_integration_setup_displayname_label',
                helpTextKey: 'aumico_abacus_step_config_displayname_help',
                type: 'text',
                required: true,
            },
            {
                key: 'serverUrl',
                labelKey: 'aumico_abacus_step_config_serverurl_label',
                helpTextKey: 'aumico_abacus_step_config_serverurl_help',
                type: 'url',
                required: true,
                placeholder: 'https://abaweb.example.ch',
            },
            {
                key: 'clientId',
                labelKey: 'aumico_abacus_step_config_clientid_label',
                type: 'text',
                required: true,
            },
            {
                key: 'clientSecret',
                labelKey: 'aumico_abacus_step_config_clientsecret_label',
                helpTextKey: 'aumico_abacus_step_config_clientsecret_help',
                type: 'password',
                required: true,
            },
        ],
    },
    {
        vendor: IntegrationVendor.TOPAL,
        displayNameKey: 'aumico_integration_topal_displayname',
        descriptionKey: 'aumico_integration_topal_description',
        logoSrc: '/assets/hamIcons/topal-logo.png',
        bannerBgColor: '#FFFFFF',
        scope: 'per-org',
        mode: ConnectionMode.CREDENTIALS_FORM,
        connectModalType: 'topal',
        // Topal exposes `GET /clients`, so companies auto-match by name (like D365 BC):
        // after the first connect the matcher pulls the client list and bulk-upserts
        // name matches, and the generic company-mapping-modal handles manual edits.
        supportsAutoMatch: true,
        // Documentation only: the actual form lives hand-coded in
        // TopalConnectModalComponent (password needs visual masking + bespoke validation).
        setupFields: [
            {
                key: 'displayName',
                labelKey: 'aumico_integration_setup_displayname_label',
                helpTextKey: 'aumico_topal_step_config_displayname_help',
                type: 'text',
                required: true,
            },
            {
                key: 'serverUrl',
                labelKey: 'aumico_topal_step_config_serverurl_label',
                helpTextKey: 'aumico_topal_step_config_serverurl_help',
                type: 'url',
                required: true,
                placeholder: 'https://topal.example.ch:9001',
            },
            {
                key: 'username',
                labelKey: 'aumico_topal_step_config_username_label',
                type: 'text',
                required: true,
            },
            {
                key: 'password',
                labelKey: 'aumico_topal_step_config_password_label',
                helpTextKey: 'aumico_topal_step_config_password_help',
                type: 'password',
                required: true,
            },
        ],
    },
    {
        vendor: IntegrationVendor.ACCOUNTO,
        displayNameKey: 'aumico_integration_accounto_displayname',
        descriptionKey: 'aumico_integration_accounto_description',
        // White word mark on a transparent PNG — it needs the dark banner.
        logoSrc: '/assets/hamIcons/accounto-logo.png',
        bannerBgColor: '#1C2236',
        scope: 'per-org',
        mode: ConnectionMode.CREDENTIALS_FORM,
        connectModalType: 'accounto',
        // The BE lists the tenants of the key (a Treuhand key its clients, a KMU key its own tenant),
        // so companies auto-match by tenant name (like Topal); the generic company-mapping-modal
        // handles manual edits.
        supportsAutoMatch: true,
        // Documentation only: the actual form lives hand-coded in
        // AccountoConnectModalComponent (the key needs masking).
        setupFields: [
            {
                key: 'displayName',
                labelKey: 'aumico_integration_setup_displayname_label',
                helpTextKey: 'aumico_accounto_step_config_displayname_help',
                type: 'text',
                required: true,
            },
            {
                key: 'apiKey',
                labelKey: 'aumico_accounto_step_config_apikey_label',
                helpTextKey: 'aumico_accounto_step_config_apikey_help',
                type: 'password',
                required: true,
            },
        ],
    },
    {
        vendor: IntegrationVendor.PROFFIX,
        displayNameKey: 'aumico_integration_proffix_displayname',
        descriptionKey: 'aumico_integration_proffix_description',
        logoSrc: '/assets/hamIcons/proffix-logo.png',
        bannerBgColor: '#FFFFFF',
        scope: 'per-org',
        mode: ConnectionMode.CREDENTIALS_FORM,
        connectModalType: 'proffix',
        // Proffix exposes `PRO/Datenbank` (the Mandanten), so companies auto-match by
        // name (like Topal): the BE returns `displayName = Bezeichnung`, which is what the
        // matcher compares, and the generic company-mapping-modal handles manual edits.
        supportsAutoMatch: true,
        // Documentation only: the actual form lives hand-coded in
        // ProffixConnectModalComponent (secrets need masking + bespoke URL validation).
        setupFields: [
            {
                key: 'displayName',
                labelKey: 'aumico_integration_setup_displayname_label',
                helpTextKey: 'aumico_proffix_step_config_displayname_help',
                type: 'text',
                required: true,
            },
            {
                key: 'serverUrl',
                labelKey: 'aumico_proffix_step_config_serverurl_label',
                helpTextKey: 'aumico_proffix_step_config_serverurl_help',
                type: 'url',
                required: true,
                placeholder: 'https://proffix.example.ch:11011/pxapi/V4',
            },
            {
                key: 'restApiKey',
                labelKey: 'aumico_proffix_step_config_restapikey_label',
                helpTextKey: 'aumico_proffix_step_config_restapikey_help',
                type: 'password',
                required: true,
            },
            {
                key: 'username',
                labelKey: 'aumico_proffix_step_config_username_label',
                type: 'text',
                required: true,
            },
            {
                key: 'password',
                labelKey: 'aumico_proffix_step_config_password_label',
                helpTextKey: 'aumico_proffix_step_config_password_help',
                type: 'password',
                required: true,
            },
        ],
    },
    {
        vendor: IntegrationVendor.SHAREPOINT,
        displayNameKey: 'aumico_integration_sharepoint_displayname',
        descriptionKey: 'aumico_integration_sharepoint_card_description',
        logoSrc: '/assets/hamIcons/sharepoint-logo.svg',
        bannerBgColor: '#E4F2F1',
        // Per-org: `GET integrations/sharepoint/connect` is @Roles(ORG_ADMIN) and builds the
        // OAuth session from `userAccount.organizationId` — there is no projectId in its DTO.
        // The per-closing folder bindings are downstream usage, not a second connection.
        scope: 'per-org',
        // Delegated authorization_code against Microsoft Entra (a real user consents in a
        // popup), unlike the client-credentials accounting vendors.
        mode: ConnectionMode.OAUTH_AUTH_CODE,
        connectModalType: 'sharepoint',
        // SharePoint has no external "companies" to enumerate at all — it is a document
        // source, so there is no company-mapping step to auto-match in the first place.
        supportsAutoMatch: false,
        // Documentation only: the form lives hand-coded in SharepointConnectModalComponent.
        // displayName is OPTIONAL here (unlike the accounting vendors) because the BE
        // defaults it to `SharePoint (<tenant-prefix>)` when omitted.
        setupFields: [
            {
                key: 'displayName',
                labelKey: 'aumico_integration_setup_displayname_label',
                helpTextKey: 'aumico_integration_sharepoint_displayname_help',
                type: 'text',
                required: false,
            },
        ],
    },
];
